"use client";

import { Label } from "@/components/ui/label";
import { type Tag, TagInput } from "emblor";
import * as React from "react";

interface TagInputFieldProps {
  id?: string;
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function TagInputField({
  id,
  label,
  value,
  onChange,
  placeholder = "Add a tag...",
  disabled = false,
  className,
}: TagInputFieldProps) {
  const internalId = React.useId();
  const componentId = id ?? internalId;

  // Internal state uses Tag objects
  const [tags, setTags] = React.useState<Tag[]>(() =>
    value.map((text, index) => ({ id: `${componentId}-tag-${index}`, text })),
  );
  const [activeTagIndex, setActiveTagIndex] = React.useState<number | null>(
    null,
  );

  // Keep track of whether we're handling an internal update
  const isInternalUpdate = React.useRef(false);

  // Effect to sync external `value` (string[]) prop to internal `tags` (Tag[]) state
  React.useEffect(() => {
    // Skip if this is an internal update
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    // Check if we need to update internal state
    const currentTagTexts = tags.map((t) => t.text);
    if (
      value.length !== currentTagTexts.length ||
      !value.every((v, i) => v === currentTagTexts[i])
    ) {
      setTags(
        value.map((text, index) => ({
          id: `${componentId}-tag-${index}`,
          text,
        })),
      );
    }
  }, [value, componentId, tags.length]); // Only depend on tags.length, not the full tags array

  // Custom handler for tag changes
  const handleTagsChange = React.useCallback(
    (newTagsOrUpdater: React.SetStateAction<Tag[]>) => {
      // Handle both direct Tag[] values and updater functions
      const newTags =
        typeof newTagsOrUpdater === "function"
          ? newTagsOrUpdater(tags)
          : newTagsOrUpdater;

      setTags(newTags);
      isInternalUpdate.current = true;
      onChange(newTags.map((tag) => tag.text));
    },
    [onChange, tags],
  );

  return (
    <div className={className}>
      <Label htmlFor={componentId} className="mb-2 block">
        {label}
      </Label>
      <TagInput
        id={componentId}
        tags={tags}
        setTags={handleTagsChange}
        activeTagIndex={activeTagIndex}
        setActiveTagIndex={setActiveTagIndex}
        placeholder={placeholder}
        disabled={disabled}
        styleClasses={{
          input:
            "border border-input bg-background text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 h-10",
          tagList: {
            container: "gap-1 mb-2 flex flex-wrap",
          },
          tag: {
            body: "relative flex items-center h-7 bg-secondary border border-border hover:bg-secondary/80 rounded-md font-semibold text-xs ps-2 pe-7 text-secondary-foreground",
            closeButton:
              "absolute -inset-y-px -end-px p-0 rounded-s-none rounded-e-md flex size-7 transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] text-muted-foreground/80 hover:text-foreground",
          },
        }}
        inlineTags={false}
        inputFieldPosition="top"
      />
    </div>
  );
}
